import { Base, withBreakpointObserver } from '@studiometa/js-toolkit';
import { gsap } from 'gsap';

/**
 * Menu component
 */
export default class Menu extends withBreakpointObserver(Base) {
  timeout = null;
  isOpen = false;
  currentSubmenu = null;

  /**
   * Component Configuration
   */
  static config = {
    name: 'Menu',
    refs: [
      'items[]',
      'submenu[]',
      'default',
      'linkItems[]',
      'pushs[]',
      'name[]',
      'menuPanel',
      'overlay',
    ],
    options: {
      inactiveBreakpoints: {
        default: 'xxs xs s',
        type: String,
      },
    },
  };

  /**
   *
   */
  mounted() {
    this.initTimeline();
  }

  /**
   * Hover on main items
   * @param {object} props Props
   * @param {object} props.target current element
   * @returns {void}
   */
  onItemsMouseenter({ target }) {
    clearTimeout(this.timeout);
    const targetIndex = target.dataset.index;

    if (this.$refs.submenu[targetIndex] === this.currentSubmenu) {
      return;
    }

    this.currentItem = target;
    this.switchItemsState();

    if (target.dataset.link !== undefined) {
      this.resetSubmenu(0, true);
      return;
    }

    if (this.currentSubmenu !== null) {
      this.currentSubmenu.classList.add('s:hidden');
    }

    this.$refs.default.classList.add('s:hidden');

    this.currentSubmenu = this.$refs.submenu[targetIndex];
    this.currentSubmenu.classList.remove('s:hidden');
  }

  /**
   * Change style of currenttab
   */
  switchItemsState() {
    this.$refs.items.forEach((element) => {
      if (this.currentItem !== element && this.currentItem !== null) {
        element.classList.add('opacity-50');
      } else {
        element.classList.add('opacity-100');
        element.classList.remove('opacity-50');
      }
    });
  }

  /**
   * When entering submenu container
   */
  onSubmenuMouseenter() {
    clearTimeout(this.timeout);
  }

  /**
   * When leaving submenu container
   */
  onSubmenuMouseleave() {
    this.resetSubmenu(1000, false);
  }

  /**
   * When leaving main items
   */
  onItemsMouseleave() {
    this.resetSubmenu(1000, false);
  }

  /**
   *
   * @param {number} delay lenght of delay.
   * @param {boolean} keepItem if link is hovered
   */
  resetSubmenu(delay, keepItem) {
    this.timeout = setTimeout(() => {
      if (this.currentSubmenu) {
        this.currentSubmenu.classList.add('s:hidden');
      }
      this.currentSubmenu = null;
      this.$refs.default.classList.remove('s:hidden');
      if (keepItem === false) {
        this.currentItem = null;
        this.switchItemsState();
      }
    }, delay);
  }

  /**
   *
   */
  initTimeline() {
    this.tl = gsap.timeline();
    this.tl.pause();
    this.tl.set(this.$el, { display: 'block' });
    this.tl.fromTo(
      this.$refs.menuPanel,
      {
        'clip-path': 'inset(0% 0% 100% 0%)',
      },
      {
        'clip-path': 'inset(0% 0% 0% 0%)',
        duration: 0.5,
        ease: 'power4.out',
      },
    );

    this.tl.to(this.$refs.overlay, {
      opacity: 1,
      duration: 0.2,
      ease: 'power4.out',
    });

    this.tl.fromTo(
      this.$refs.name,
      {
        opacity: 0,
        yPercent: 100,
      },
      {
        yPercent: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.05,
        ease: 'power4.out',
      },
      '>-=0.4',
    );

    this.tl.fromTo(
      this.$refs.pushs,
      {
        opacity: 0,
        y: -30,
      },
      {
        y: 0,
        opacity: 1,
        duration: 1,
        stagger: 0.07,
        ease: 'power4.out',
      },
      '>-=0.8',
    );
  }

  /**
   * Open Menu
   */
  open() {
    document.body.classList.add('menu-is-open');

    if (this.isOpen) {
      return;
    }
    this.isOpen = true;
    this.tl.play(0);
  }

  /**
   * Close menu
   */
  close() {
    document.body.classList.remove('menu-is-open');
    if (this.isOpen) {
      gsap.to(this.$refs.overlay, {
        opacity: 0,
        duration: 0.1,
        ease: 'power4.out',
      });
      gsap.to(this.$refs.menuPanel, {
        'clip-path': 'inset(0% 0% 100% 0%)',
        duration: 0.5,
        ease: 'power4.out',
        onComplete: () => {
          this.isOpen = false;
          gsap.set(this.$el, { display: 'none' });
        },
      });
    }
  }

  /**
   * Simply toggle menu state
   */
  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Close panel when type on Escape key
   * @param {object} props Props
   * @param {object} props.ESC Escape key
   * @returns {void}
   */
  keyed({ ESC }) {
    if (ESC) {
      this.close();
    }
  }
}
